import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { teamApi, userManagementApi } from '../../../services/api';
import { Team, User } from '../../../types';

interface TeamFormProps {
  team?: Team;
  onClose: () => void;
  onSuccess: () => void;
}

const TeamForm: React.FC<TeamFormProps> = ({ team, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: team?.name || '',
    description: team?.description || '',
    member_ids: team?.members?.map(m => m.id) || [] as number[],
  });
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoadingUsers(true);
        const response = await userManagementApi.list({ per_page: 100 });
        setUsers(response.data);
      } catch (error) {
        console.error('Failed to load users:', error);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleUserToggle = (userId: number) => {
    setFormData((prev) => {
      const memberIds = prev.member_ids || [];
      if (memberIds.includes(userId)) {
        return {
          ...prev,
          member_ids: memberIds.filter(id => id !== userId),
        };
      } else {
        return {
          ...prev,
          member_ids: [...memberIds, userId],
        };
      }
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      if (team) {
        await teamApi.update(team.id, {
          name: formData.name,
          description: formData.description || undefined,
        });
        // Update members separately
        const currentMemberIds = team.members?.map(m => m.id) || [];
        const toAdd = formData.member_ids.filter(id => !currentMemberIds.includes(id));
        const toRemove = currentMemberIds.filter(id => !formData.member_ids.includes(id));
        
        if (toAdd.length > 0) {
          await teamApi.addMembers(team.id, toAdd);
        }
        if (toRemove.length > 0) {
          await teamApi.removeMembers(team.id, toRemove);
        }
      } else {
        await teamApi.create({
          name: formData.name,
          description: formData.description || undefined,
          member_ids: formData.member_ids,
        });
      }
      onSuccess();
    } catch (error: any) {
      console.error('Failed to save team:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to save team';
      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        alert(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] pt-20 pb-8 px-4" onClick={onClose}>
      <div className="theme-bg-primary rounded-lg shadow-xl w-full max-w-2xl max-h-[calc(100vh-8rem)] overflow-y-auto theme-border" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 theme-bg-primary border-b theme-border px-4 py-2.5 flex items-center justify-between">
          <h2 className="text-lg font-semibold theme-text-primary">
            {team ? 'Edit Team' : 'Create Team'}
          </h2>
          <button
            onClick={onClose}
            className="theme-text-secondary hover:opacity-80 transition-opacity"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div>
            <label htmlFor="name" className="block text-sm font-medium theme-text-primary mb-0.5">
              Team Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-2 py-1 rounded text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent ${
                errors.name ? 'border-red-500' : 'theme-border'
              }`}
              placeholder="Enter team name"
            />
            {errors.name && (
              <p className="mt-0.5 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium theme-text-primary mb-0.5">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={2}
              className="w-full px-2 py-1 theme-border rounded text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter team description (optional)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">
              Team Members
            </label>
            {loadingUsers ? (
              <div className="text-sm theme-text-tertiary">Loading users...</div>
            ) : (
              <div className="theme-border rounded p-2 max-h-48 overflow-y-auto theme-bg-primary">
                {users.length === 0 ? (
                  <p className="text-sm theme-text-tertiary">No users available</p>
                ) : (
                  <div className="space-y-1.5">
                    {users.map((user) => (
                      <label
                        key={user.id}
                        className="flex items-center gap-2 p-1.5 hover:opacity-80 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.member_ids.includes(user.id)}
                          onChange={() => handleUserToggle(user.id)}
                          className="w-4 h-4 text-blue-600 theme-border rounded focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm theme-text-primary">{user.name}</div>
                          <div className="text-sm theme-text-secondary">{user.email}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            <p className="mt-1.5 text-sm theme-text-secondary">
              {formData.member_ids.length} member{formData.member_ids.length !== 1 ? 's' : ''} selected
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t theme-border">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm theme-border rounded theme-bg-secondary theme-text-primary hover:opacity-80 transition-opacity"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-3 py-1.5 text-sm bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : team ? 'Update Team' : 'Create Team'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default TeamForm;

